import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, getCategoryName, getVideosByCategory } from '@/mocks/videos';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const categoryName = getCategoryName(slug || '');
  const videos = getVideosByCategory(slug || '');

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        {/* Breadcrumb */}
        <div className="bg-background-50 border-b border-background-200/70">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: categoryName },
            ]}
          />
        </div>

        {/* Category Header */}
        <section className="w-full bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-12">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-3">
              {categoryName}
            </h1>
            <p className="text-sm text-foreground-600">
              共 {videos.length} 部{categoryName}资源，高清流畅在线观看
            </p>

            {/* Category Tabs */}
            <div className="flex items-center gap-2 mt-6 overflow-x-auto pb-2">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    slug === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-50 text-foreground-700 hover:bg-background-200 border border-background-200/70'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Video Grid */}
        <section className="w-full px-4 md:px-6 lg:px-8 py-8 md:py-10 bg-background-50">
          <div className="max-w-7xl mx-auto">
            {videos.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {videos.map((video) => (
                  <Link
                    key={video.id}
                    to={`/article/${video.id}`}
                    className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 cursor-pointer transition-all hover:scale-[1.02]"
                  >
                    <div className="relative aspect-video overflow-hidden">
                      <img
                        src={video.thumbnail}
                        alt={video.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                      <div className="absolute top-2 left-2">
                        <span className="px-2 py-0.5 text-[10px] font-medium bg-primary-500 text-white rounded-full whitespace-nowrap">
                          {video.rating}
                        </span>
                      </div>
                      {video.isNew && (
                        <div className="absolute top-2 right-2">
                          <span className="px-2 py-0.5 text-[10px] font-medium bg-accent-500 text-white rounded-full whitespace-nowrap">
                            NEW
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="p-3">
                      <h3 className="font-heading font-semibold text-foreground-950 text-sm mb-1.5 group-hover:text-primary-600 transition-colors line-clamp-1">
                        {video.title}
                      </h3>
                      <div className="flex items-center justify-between text-xs text-foreground-500">
                        <span>{video.source}</span>
                        <span>{video.year}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-full">
                  <i className="ri-film-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-600">该分类暂无内容</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}